/**
 * ShopTrackOrder — Guest/public order tracking (no account required)
 * URL: /shop/:businessId/track?email=...&order=...
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import API_URL from '../../config/api';

type TrackedOrder = {
  id: number;
  order_number: string;
  status: string;
  total: number;
  payment_method?: string;
  payment_status?: string;
  created_at?: string;
  customer_name?: string;
  items?: unknown;
};

const ShopTrackOrder: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [orderNumber, setOrderNumber] = useState(searchParams.get('order') || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [order, setOrder] = useState<TrackedOrder | null>(null);

  const lookup = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError('');
    setOrder(null);
    if (!email.trim() || !orderNumber.trim()) {
      setError('Enter the email and order number from your confirmation.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/shop-auth/track-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ businessId, email: email.trim(), orderNumber: orderNumber.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Order not found');
        return;
      }
      setOrder(data.order);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchParams.get('email') && searchParams.get('order')) {
      lookup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let items: Array<{ productName?: string; quantity?: number; unitPrice?: number }> = [];
  if (order?.items) {
    try {
      items = typeof order.items === 'string' ? JSON.parse(order.items) : order.items;
      if (!Array.isArray(items)) items = [];
    } catch {
      items = [];
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f0f4ff', fontFamily: 'system-ui, sans-serif', padding: '24px 16px' }}>
      <div style={{ maxWidth: 520, margin: '0 auto' }}>
        <Link to={`/shop/${businessId}`} style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none' }}>
          ← Back to shop
        </Link>

        <div style={{ background: '#fff', borderRadius: 16, padding: '32px 28px', marginTop: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>📦</div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#111827' }}>Track your order</h1>
            <p style={{ margin: '8px 0 0', fontSize: 14, color: '#6b7280' }}>
              No account needed — use the email and order number from your confirmation.
            </p>
          </div>

          <form onSubmit={lookup}>
            <label style={{ display: 'block', marginBottom: 14 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{ width: '100%', marginTop: 6, padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', boxSizing: 'border-box' }}
              />
            </label>
            <label style={{ display: 'block', marginBottom: 16 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Order number</span>
              <input
                type="text"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder="e.g. SHOP-1730..."
                required
                style={{ width: '100%', marginTop: 6, padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', boxSizing: 'border-box' }}
              />
            </label>
            {error && <p style={{ color: '#b91c1c', fontSize: 13, marginBottom: 12 }}>{error}</p>}
            <button
              type="submit"
              disabled={loading}
              style={{ width: '100%', padding: 12, borderRadius: 10, border: 'none', background: '#1d4ed8', color: '#fff', fontWeight: 700, cursor: loading ? 'wait' : 'pointer' }}
            >
              {loading ? 'Looking up…' : 'Track order'}
            </button>
          </form>

          {order && (
            <div style={{ marginTop: 28, paddingTop: 24, borderTop: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Order</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>{order.order_number || `#${order.id}`}</div>
                </div>
                <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
                  {order.status}
                </span>
              </div>
              <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, marginBottom: 16 }}>
                <div><strong>Total:</strong> £{Number(order.total || 0).toFixed(2)}</div>
                {order.payment_method && <div><strong>Payment:</strong> {order.payment_method}{order.payment_status ? ` — ${order.payment_status}` : ''}</div>}
                {order.created_at && <div><strong>Placed:</strong> {new Date(order.created_at).toLocaleString('en-GB')}</div>}
              </div>
              {items.length > 0 && (
                <div style={{ background: '#f9fafb', borderRadius: 10, padding: '12px 14px' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 8 }}>Items</div>
                  {items.map((item, i) => (
                    <div key={i} style={{ fontSize: 13, color: '#374151', padding: '4px 0' }}>
                      {item.quantity || 1}× {item.productName || 'Product'}
                      {item.unitPrice != null ? ` — £${Number(item.unitPrice).toFixed(2)}` : ''}
                    </div>
                  ))}
                </div>
              )}
              <p style={{ marginTop: 16, fontSize: 12, color: '#6b7280' }}>
                Have a trade account? <Link to={`/shop/${businessId}/login`}>Log in</Link> to see all your orders in one place.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShopTrackOrder;
